import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  resetPassword as amplifyResetPassword,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  confirmResetPassword as amplifyConfirmResetPassword,
} from 'aws-amplify/auth';

import { configureAmplify } from './amplify-config';

// ----------------------------------------------------------------------

export type SignInParams = {
  email: string;
  password: string;
};

export type SignUpParams = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

export type ConfirmSignUpParams = {
  email: string;
  code: string;
};

export type ConfirmPasswordResetParams = {
  email: string;
  code: string;
  password: string;
};

/** **************************************
 * Sign in
 *************************************** */
export const signInWithPassword = async ({ email, password }: SignInParams) => {
  if (!configureAmplify()) {
    throw new Error('Amplify is not configured');
  }

  return amplifySignIn({ username: email, password });
};

/** **************************************
 * Sign up
 *************************************** */
export const signUp = async ({ email, password, firstName, lastName }: SignUpParams) => {
  if (!configureAmplify()) {
    throw new Error('Amplify is not configured');
  }

  return amplifySignUp({
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        given_name: firstName,
        family_name: lastName,
      },
    },
  });
};

/** **************************************
 * Confirm sign up
 *************************************** */
export const confirmSignUp = async ({ email, code }: ConfirmSignUpParams) => {
  if (!configureAmplify()) {
    throw new Error('Amplify is not configured');
  }

  return amplifyConfirmSignUp({ username: email, confirmationCode: code });
};

/** **************************************
 * Resend sign up code
 *************************************** */
export const resendSignUpCode = async (email: string) => {
  if (!configureAmplify()) {
    throw new Error('Amplify is not configured');
  }

  return amplifyResendSignUpCode({ username: email });
};

/** **************************************
 * Request password reset
 *************************************** */
export const requestPasswordReset = async (email: string) => {
  if (!configureAmplify()) {
    throw new Error('Amplify is not configured');
  }

  return amplifyResetPassword({ username: email });
};

/** **************************************
 * Confirm password reset
 *************************************** */
export const confirmPasswordReset = async ({
  email,
  code,
  password,
}: ConfirmPasswordResetParams) => {
  if (!configureAmplify()) {
    throw new Error('Amplify is not configured');
  }

  return amplifyConfirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword: password,
  });
};

/** **************************************
 * Sign out
 *************************************** */
export const signOut = async (): Promise<void> => {
  if (!configureAmplify()) {
    return;
  }

  await amplifySignOut();
};
